import { Card, btnPrimary, btnSecondary, btnDanger, inputStyle, Badge } from "../shared";
import { ROLE_COLOR, ROLE_LABEL, ROLE_DESC, ADDON_LIMIT } from "./constants";
import { InviteModal } from "./InviteModal";

function MemberAvatar({ name, email }) {
  const initial = ((name || email || "?")[0]).toUpperCase();
  return (
    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#EAF2ED", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 17, color: "#163C2F" }}>{initial}</span>
    </div>
  );
}

export function TeamSection({
  teamMembers, isOwner, isPaid, companyProfile,
  newMember, setNewMember,
  showAddMember, setShowAddMember,
  addTeamMember, resendInvite, removeTeamMember, updateMemberRole,
}) {
  const activeMembers  = teamMembers.filter(m => !m.status || m.status === "active");
  const pendingMembers = teamMembers.filter(m => m.status === "pending");
  const teamAddon      = companyProfile?.team_add_on || "none";
  const addonLimit     = isOwner ? Infinity : (ADDON_LIMIT[teamAddon] ?? 0);
  const atLimit        = !isOwner && isPaid && activeMembers.length >= addonLimit;
  const needsAddon     = !isOwner && isPaid && teamAddon === "none";
  const needsBasePlan  = !isOwner && !isPaid;

  const handleInviteClick = () => {
    if (needsBasePlan) { alert("Team members require a Core or Pro base plan. Upgrade in Settings → Profile."); return; }
    if (needsAddon)    { alert("Add a Team Members add-on in Settings → Profile → Team Members to invite people."); return; }
    if (atLimit)       { alert(`You've reached your ${addonLimit}-member limit. Upgrade your team add-on to invite more members.`); return; }
    setShowAddMember(true);
  };

  return (
    <>
      <div style={{ marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: 32, color: "#2B2B2B", margin: 0 }}>Team</h1>
          <div style={{ color: "#9B9690", marginTop: 4, fontSize: 14 }}>
            {activeMembers.length} active · {pendingMembers.length} pending invite{pendingMembers.length !== 1 ? "s" : ""}
            {addonLimit !== Infinity && isPaid && teamAddon !== "none" && (
              <span style={{ marginLeft: 8, color: atLimit ? "#dc2626" : "#9B9690" }}>
                · {activeMembers.length}/{addonLimit} member slots used
              </span>
            )}
          </div>
        </div>
        <button onClick={handleInviteClick} style={{ ...btnPrimary, opacity: (needsBasePlan || needsAddon || atLimit) ? 0.6 : 1 }}>
          + Invite Member
        </button>
      </div>
      {(needsBasePlan || needsAddon) && (
        <div style={{ background: "#FEF3C7", border: "1px solid #F59E0B44", borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#92400E" }}>
          {needsBasePlan
            ? "⚠️ Team members require a Core or Pro plan. Upgrade your base plan in the Profile tab."
            : "⚠️ Add a Team Members add-on in the Profile tab to start inviting team members."}
        </div>
      )}

      {/* Role legend */}
      <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
        {Object.entries(ROLE_DESC).map(([role, desc]) => (
          <div key={role} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", background: "#fff", border: "1px solid #E8E4DC", borderRadius: 8, fontSize: 12 }}>
            <Badge label={ROLE_LABEL[role]} color={ROLE_COLOR[role]} />
            <span style={{ color: "#9B9690" }} dangerouslySetInnerHTML={{ __html: desc }} />
          </div>
        ))}
      </div>

      {/* Active members */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 18, color: "#163C2F", marginBottom: 16 }}>
          Active Members <span style={{ fontSize: 13, fontWeight: 400, color: "#9B9690" }}>({activeMembers.length})</span>
        </div>
        {activeMembers.length === 0 ? (
          <div style={{ padding: "28px 0", textAlign: "center", color: "#9B9690" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>🏢</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: "#163C2F", marginBottom: 6 }}>No active members yet</div>
            <div style={{ fontSize: 13, marginBottom: 18 }}>Invite a team member — they'll appear here once they accept.</div>
            <button onClick={() => setShowAddMember(true)} style={btnPrimary}>+ Invite Your First Member</button>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #E8E4DC" }}>
                {["Member", "Email", "Role", ""].map((h, i) => (
                  <th key={i} style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, color: "#9B9690", fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activeMembers.map(m => (
                <tr key={m.id} style={{ borderBottom: "1px solid #F5F1EA" }}>
                  <td style={{ padding: "12px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <MemberAvatar name={m.name} email={m.email} />
                      <span style={{ fontWeight: 600, fontSize: 14, color: "#2B2B2B" }}>{m.name || "—"}</span>
                    </div>
                  </td>
                  <td style={{ padding: "12px 12px", fontSize: 13, color: "#9B9690" }}>{m.email}</td>
                  <td style={{ padding: "12px 12px" }}>
                    <select
                      value={m.role}
                      onChange={e => updateMemberRole(m.id, e.target.value)}
                      style={{ ...inputStyle, padding: "5px 10px", fontSize: 13, width: "auto", minWidth: 130 }}
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="view_only">View Only</option>
                    </select>
                  </td>
                  <td style={{ padding: "12px 12px", textAlign: "right" }}>
                    <button onClick={() => removeTeamMember(m.id, false)} style={{ ...btnDanger, fontSize: 12, padding: "5px 12px" }}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Pending invites */}
      {pendingMembers.length > 0 && (
        <Card>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 18, color: "#163C2F", marginBottom: 16 }}>
            Pending Invites <span style={{ fontSize: 13, fontWeight: 400, color: "#9B9690" }}>({pendingMembers.length})</span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #E8E4DC" }}>
                {["Email", "Role", "Invited", ""].map((h, i) => (
                  <th key={i} style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, color: "#9B9690", fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pendingMembers.map(m => (
                <tr key={m.id} style={{ borderBottom: "1px solid #F5F1EA" }}>
                  <td style={{ padding: "12px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#F5F1EA", border: "1.5px dashed #d4cfc4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontSize: 14, color: "#B0ACA6" }}>✉️</span>
                      </div>
                      <span style={{ fontSize: 14, color: "#2B2B2B" }}>{m.email}</span>
                    </div>
                  </td>
                  <td style={{ padding: "12px 12px" }}>
                    <Badge label={ROLE_LABEL[m.role] || m.role} color={ROLE_COLOR[m.role] || "#9B9690"} />
                  </td>
                  <td style={{ padding: "12px 12px", fontSize: 12, color: "#9B9690" }}>
                    {m.invited_at
                      ? new Date(m.invited_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                      : "—"}
                  </td>
                  <td style={{ padding: "12px 12px" }}>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <button
                        onClick={() => resendInvite(m)}
                        style={{ ...btnSecondary, fontSize: 12, padding: "5px 12px" }}
                      >
                        Resend
                      </button>
                      <button
                        onClick={() => removeTeamMember(m.id, true)}
                        style={{ ...btnDanger, fontSize: 12, padding: "5px 12px" }}
                      >
                        Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {showAddMember && (
        <InviteModal
          newMember={newMember}
          setNewMember={setNewMember}
          addTeamMember={addTeamMember}
          setShowAddMember={setShowAddMember}
        />
      )}
    </>
  );
}
